import React, { useEffect, useState } from "react";
import { Formik, Field } from "formik";
import { DeliveryTaskShotInput, UserTaskShot } from "@/__generated__/graphql";
import { InjectedHttpUploadProps } from "@/components/common/HttpUpload/HttpUpload";
import { useUserContext } from "@/contexts/UserContextProvider";
import { formatShotWithSubServiceName } from "@/helpers/utils";
import {
  createDirectoryStructure,
  getSignedURLsForUserDelivery,
} from "@/components/common/FileUpload/helpers";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import ServiceDots, {
  getServiceOrSubserviceBasedOnSubservice,
} from "@/components/common/ServiceDots";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TextareaField } from "@/components/common/FormikFIelds/TextareaField/TextareaField";
import FileUploadField from "@/components/common/FileUpload/FileUpload";
import ErrorMessage from "@/components/common/Error";
import { cn } from "@/lib/utils";

export interface VendorDeliveryFormValues {
  notes: string;
  attachments: string[];
  taskShotNotes: Record<string, string>;
}

interface UserDeliveryConfirmProps {
  folderPath: string;
  projectId: string;
  httpFiles: File[];
  deliveryKeysToTaskShotIds: DeliveryTaskShotInput[];
  taskShots: UserTaskShot[];
  onSubmit: (
    deliveryPath: string,
    notes?: string,
    attachments?: string[],
    folderPathOrDeliveryPath?: string,
  ) => Promise<void> | void;
  uploadProps: InjectedHttpUploadProps;
  onBack?: () => void;
}

const UserDeliveryConfirm: React.FC<UserDeliveryConfirmProps> = ({
  folderPath: _folderPath,
  projectId: _projectId,
  httpFiles: _httpFiles,
  deliveryKeysToTaskShotIds,
  taskShots,
  onSubmit,
  uploadProps,
  onBack,
}) => {
  const { currentProject } = useUserContext();
  const [deliveryPath, setDeliveryPath] = useState<string>("");

  useEffect(() => {
    if (
      uploadProps &&
      _httpFiles.length &&
      !uploadProps.isUploadStarted &&
      typeof uploadProps.startUpload === "function"
    ) {
      const { startUpload } = uploadProps;
      try {
        startUpload(
          _projectId,
          getSignedURLsForUserDelivery,
          _httpFiles,
          true,
        ).then((url: string) => {
          if (url) setDeliveryPath(url);
        });
      } catch (error) {
        console.error("Error starting upload:", error);
      }
    }
  }, [_httpFiles, uploadProps, _projectId]);

  const deliveries = deliveryKeysToTaskShotIds.reduce(
    (acc, delivery) => {
      const userTaskShot = taskShots.find(
        (ts) => ts.id === delivery.taskShotId,
      );
      if (userTaskShot) {
        if (!acc[userTaskShot.id]) acc[userTaskShot.id] = [];
        acc[userTaskShot.id].push(delivery);
      }
      return acc;
    },
    {} as Record<string, DeliveryTaskShotInput[]>,
  );

  const getLinkedMediaDisplayText = (
    deliveriesForTaskShot: DeliveryTaskShotInput[],
  ) => {
    if (!deliveriesForTaskShot?.length) return null;
    const directoryStructure = createDirectoryStructure(
      deliveriesForTaskShot.map((delivery) => delivery.key),
    );
    return directoryStructure.map((directory) => (
      <div key={directory.absolutePath}>
        <LongContentWrapper>{directory.name}</LongContentWrapper>
      </div>
    ));
  };

  return (
    <Formik<VendorDeliveryFormValues>
      isInitialValid
      initialValues={{ notes: "", attachments: [], taskShotNotes: {} }}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        try {
          const pathToSend = deliveryPath || _folderPath || "";
          await onSubmit(
            deliveryPath,
            values.notes,
            values.attachments,
            pathToSend,
          );
        } catch (error) {
          console.error(error);
          setStatus({
            formError: `Error: ${String(error)}`,
            formSuccess: "",
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, isValid, submitForm, status }) => (
        <div className="flex flex-col h-full">
          <div className="p-6 bg-quinary rounded-lg shadow-sm flex-grow overflow-y-auto scroll-visible">
            <div className="flex items-start">
              <Table className="table-fixed !border-spacing-x-3 !border-spacing-y-4 border-separate flex-1">
                <TableHeader className="text-sm font-medium text-secondary-foreground sticky top-0 bg-transparent">
                  <TableRow className="bg-transparent hover:bg-transparent border-none [&_th]:py-3 [&_th]:px-0">
                    <TableHead className="w-[30%]">Shot name</TableHead>
                    <TableHead className="w-[15%]">Service</TableHead>
                    <TableHead className="w-[30%]">Linked Media</TableHead>
                    <TableHead className="w-[20%]">Notes</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody className="text-sm text-primary-foreground">
                  {taskShots.map((ts) => {
                    const shotName = formatShotWithSubServiceName(
                      ts.taskShot.shot.name,
                      ts.taskShot.subService,
                    );

                    const deliveriesForTaskShot = deliveries[ts.id] || [];
                    const directoryStructure = createDirectoryStructure(
                      deliveriesForTaskShot.map((delivery) => delivery.key),
                    );

                    if (!directoryStructure.length) return null;

                    return (
                      <TableRow
                        key={ts.id}
                        className={cn(
                          "border-none bg-transparent hover:bg-transparent [&>td]:py-3 [&>td]:px-4 [&_td]:rounded-lg",
                        )}
                      >
                        <TableCell>
                          <LongContentWrapper>{shotName}</LongContentWrapper>
                        </TableCell>

                        <TableCell className="border-none px-2">
                          <ServiceDots
                            transparent
                            colorBorder
                            square
                            colorText
                            services={getServiceOrSubserviceBasedOnSubservice(
                              [ts.taskShot.task.service],
                              ts.taskShot.subService,
                            )}
                            innerClassName="w-full !py-2 text-[16px]"
                          />
                        </TableCell>

                        <TableCell>
                          {getLinkedMediaDisplayText(deliveriesForTaskShot)}
                        </TableCell>

                        <TableCell className="border-none">
                          <Field
                            name={`taskShotNotes.${ts.id}`}
                            component={TextareaField}
                            placeholder="Type your note"
                            inputClassName="bg-tertiary rounded-md px-3 py-2 focus:outline-none w-full resize-none"
                            rows={1}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex flex-col gap-4 border-l px-6 ml-3 w-[300px]">
                <div className="font-medium text-secondary">Add a note</div>

                <div className="relative">
                  <Field
                    name="notes"
                    component={TextareaField}
                    placeholder="Type your note"
                    inputClassName="bg-tertiary rounded-md px-3 py-2 focus:outline-none w-full resize-none"
                    rows={3}
                  />
                  <Field
                    name="attachments"
                    component={FileUploadField}
                    projectId={currentProject?.id}
                    isNotesAttachment
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 bg-primary border-t flex justify-end gap-4 py-4 px-6 rounded-b-lg">
            <ErrorMessage error={status?.formError} />

            <Button
              variant="secondary"
              onClick={() => {
                if (uploadProps && !uploadProps.cancelUploadAlert()) return;
                if (onBack) onBack();
              }}
            >
              Back
            </Button>

            <Button
              onClick={() => submitForm()}
              disabled={
                isSubmitting ||
                !isValid ||
                (uploadProps && !uploadProps.isUploadCompleted)
              }
              className="bg-service-paint hover:bg-service-paint/90 text-white"
            >
              Submit
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default UserDeliveryConfirm;
